import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ClipboardPaste, Contact } from "lucide-react";
import { G, errMsg } from "./guestApi";
import { GuestList } from "./GuestList";
import { BulkPasteDialog } from "./BulkPasteDialog";
import { ImportUpload } from "./ImportUpload";
import { ImportReview } from "./ImportReview";
import { ImportHistory } from "./ImportHistory";
import { AudiencePreview } from "./AudiencePreview";

export default function GuestContactsTab({ session }) {
  const [centres, setCentres] = useState([]);
  const [center, setCenter] = useState("");
  const [sub, setSub] = useState("contacts");
  const [batchId, setBatchId] = useState(null);
  const [paste, setPaste] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    api.post(`${G}/centres`, { token: session.token }).then((r) => {
      setCentres(r.data.rows || []);
      if (r.data.rows?.length) setCenter(r.data.rows[0].code);
    }).catch((e) => toast.error(errMsg(e)));
  }, [session.token]);

  const openBatch = (id) => { setBatchId(id); setSub("import"); };
  const closeBatch = () => { setBatchId(null); setRefreshKey((k) => k + 1); };

  if (!centres.length) {
    return <div className="text-sm text-slate-500 p-4" data-testid="guest-no-centres">No centres are assigned to your login.</div>;
  }
  return (
    <div className="space-y-3" data-testid="guest-contacts-tab">
      <div className="flex flex-wrap items-center gap-2">
        <Contact className="w-5 h-5 text-emerald-700" />
        <h2 className="text-base font-semibold">Guest Contacts</h2>
        <Select value={center} onValueChange={(v) => { setCenter(v); setBatchId(null); }}>
          <SelectTrigger className="w-60" data-testid="guest-centre-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {centres.map((c) => <SelectItem key={c.code} value={c.code}>{c.code} · {c.region}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button size="sm" variant="outline" className="ml-auto" onClick={() => setPaste(true)}
          data-testid="guest-bulk-paste-btn"><ClipboardPaste className="w-4 h-4 mr-1" />Bulk paste</Button>
      </div>
      <Tabs value={sub} onValueChange={setSub}>
        <TabsList>
          <TabsTrigger value="contacts" data-testid="guest-subtab-contacts">Contacts</TabsTrigger>
          <TabsTrigger value="import" data-testid="guest-subtab-import">Import</TabsTrigger>
          <TabsTrigger value="history" data-testid="guest-subtab-history">Import history</TabsTrigger>
          {centres.length > 1 && <TabsTrigger value="audience" data-testid="guest-subtab-audience">Audience check</TabsTrigger>}
        </TabsList>
        <TabsContent value="contacts">
          <GuestList session={session} center={center} refreshKey={refreshKey} />
        </TabsContent>
        <TabsContent value="import">
          {batchId ? <ImportReview session={session} batchId={batchId} onClose={closeBatch} />
            : <ImportUpload session={session} center={center} onStaged={setBatchId} />}
        </TabsContent>
        <TabsContent value="history">
          <ImportHistory session={session} center={center} refreshKey={refreshKey} onOpen={openBatch} />
        </TabsContent>
        {centres.length > 1 && (
          <TabsContent value="audience"><AudiencePreview session={session} centres={centres} /></TabsContent>)}
      </Tabs>
      <BulkPasteDialog open={paste} onClose={() => setPaste(false)} session={session} center={center}
        onStaged={openBatch} />
    </div>
  );
}
